/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';
import { FocusScope } from 'react-aria';

import { Button, Heading } from 'sarsaparilla';

import styles from './CloseModalConfirmation.module.scss';

interface CloseModalConfirmationProps {
    isOpen: boolean;
    onAbortCloseClick: () => void;
    onCloseClick: () => void;
}

export function CloseModalConfirmation({
    isOpen,
    onAbortCloseClick,
    onCloseClick,
}: CloseModalConfirmationProps) {
    const alertRef = React.useRef<HTMLDivElement>(null);
    const continueButtonRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onAbortCloseClick();
            }
        };

        const handleClickOutside = (e: MouseEvent) => {
            if (alertRef.current && !alertRef.current.contains(e.target as Node)) {
                onAbortCloseClick();
            }
        };

        const timeoutId = setTimeout(() => {
            // Delay adding click listener to avoid catching the click that opened the modal
            window.addEventListener('click', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
        }, 0);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('click', handleClickOutside);
            clearTimeout(timeoutId);
        };
    }, [isOpen, onAbortCloseClick]);

    return (
        <>
            <div
                className={cx(styles.closeModalConfirmation, isOpen && styles.visible)}
                aria-live="assertive"
            >
                <div
                    ref={alertRef}
                    className={styles.alert}
                    tabIndex={isOpen ? -1 : undefined}
                    aria-hidden={!isOpen}
                    role={isOpen ? 'dialog' : undefined}
                    aria-labelledby={isOpen ? 'confirmation-title' : undefined}
                >
                    {isOpen ? (
                        <FocusScope contain restoreFocus autoFocus>
                            <div className={styles.alertContent}>
                                <Heading
                                    headingLevel={1}
                                    appearance="h6"
                                    id="confirmation-title"
                                >
                                    Leave Account Setup?
                                </Heading>
                                <div className="text-sm color-text-03 mt-half">
                                    You will lose your progress if you close this window.
                                </div>
                            </div>
                            <div className={styles.buttonGroup}>
                                <Button
                                    size="sm"
                                    appearance="tertiary-alt"
                                    onClick={onAbortCloseClick}
                                    innerRef={continueButtonRef}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    appearance="danger"
                                    onClick={onCloseClick}
                                >
                                    Leave
                                </Button>
                            </div>
                        </FocusScope>
                    ) : null}
                </div>
            </div>
        </>
    );
}
